import React, { createContext, useState, useEffect } from 'react';
import { supabase } from './data/supabaseClient';
import i18n from '../i18n';

export const AuthContext = createContext();

/**
 * Theme application logic - simplified for design-system.css
 *
 * All color theming is now handled by design-system.css using [data-theme] selectors.
 * Legacy color variables (--main-color, --BackG-color, etc.) are mapped within
 * design-system.css for each theme, eliminating the need for JavaScript-based
 * color variable updates.
 *
 * Supported themes:
 * - option1: Default Theme (Mint/Teal aesthetic)
 * - option2: Dark Theme (Dark mode with teal accents)
 * - option3: Light Theme (Warm beige/gray aesthetic)
 */
const applyThemeStyles = (themeOption) => {
  console.log('🎨 Applying theme:', themeOption);

  // Set data-theme attribute - this triggers all color changes in design-system.css
  document.documentElement.setAttribute('data-theme', themeOption);

  console.log('✅ Theme applied via data-theme attribute');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    
    // Force loading to end after timeout to prevent infinite loading
    const safetyTimeout = setTimeout(() => {
      if (isMounted) {
        console.log('Safety timeout triggered - forcing loading to complete');
        setLoading(false);
        setProfileLoading(false);
      }
    }, 3000);

    const fetchUserAndInitialize = async () => {
      try {
        console.log('Fetching authenticated session...');
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Session error:', sessionError);
          if (isMounted) {
            setLoading(false);
            setProfileLoading(false);
          }
          return;
        }

        const session = sessionData?.session;
        const currentUser = session?.user || null;
        
        // Handle case where user is logged in
        if (currentUser) {
          console.log('User is logged in, ID:', currentUser.id);
          
          // Set user immediately, don't wait for profile
          if (isMounted) setUser(currentUser);
          
          try {
            // Set default language and theme immediately in case profile fetch hangs
            await i18n.changeLanguage(
              localStorage.getItem('preferredLanguage') || i18n.language || 'en'
            );
            
            // Apply theme from localStorage if available
            const savedTheme = localStorage.getItem('theme') || 'option1';
            applyTheme(savedTheme);
            
            // Now try to get user's full profile including location data
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', currentUser.id)
              .single();
              
            if (profileError) {
              console.warn('Could not fetch user profile:', profileError);
              // Continue with default preferences
            } else if (profile) {
              if (isMounted) {
                setUserProfile(profile);
                
                // Apply language preference from profile
                if (profile.language) {
                  console.log('Setting language to user preference:', profile.language);
                  await i18n.changeLanguage(profile.language);
                  localStorage.setItem('preferredLanguage', profile.language);
                }
                
                // Apply theme preference from profile
                if (profile.theme_preference) {
                  console.log('Setting theme to user preference:', profile.theme_preference);
                  applyTheme(profile.theme_preference);
                  localStorage.setItem('theme', profile.theme_preference);
                }
              }
            }
          } catch (error) {
            console.error('Error setting preferences:', error);
          } finally {
            // Always end loading, even if preference setting fails
            if (isMounted) {
              setLoading(false);
              setProfileLoading(false);
            }
          }
        } else {
          // No user logged in - simpler path
          console.log('No user logged in');
          if (isMounted) {
            setUser(null);
            setUserProfile(null);
            setLoading(false);
            setProfileLoading(false);
            
            // Apply preferences from localStorage for non-logged in users
            const savedLanguage = localStorage.getItem('preferredLanguage');
            if (savedLanguage) {
              await i18n.changeLanguage(savedLanguage);
            }
            
            const savedTheme = localStorage.getItem('theme');
            if (savedTheme) {
              applyTheme(savedTheme);
            }
          }
        }
      } catch (e) {
        console.error('Unexpected error in auth initialization:', e);
        if (isMounted) {
          setLoading(false);
          setProfileLoading(false);
        }
      }
    };

    fetchUserAndInitialize();

    // Auth listener (simplified and focused on safety)
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log(`Auth event: ${event}`);
      if (isMounted) {
        const newUser = session?.user || null;
        setUser(newUser);
        
        // Clear profile data on sign out
        if (event === 'SIGNED_OUT' || !newUser) {
          setUserProfile(null);
        }
      }
    });

    // Cleanup
    return () => {
      isMounted = false;
      clearTimeout(safetyTimeout);
      if (authListener?.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  // Apply theme based on option ID
  const applyTheme = (themeOption) => {
    // Apply the theme styles immediately
    applyThemeStyles(themeOption);
    
    // Dispatch event for any components listening for theme changes
    window.dispatchEvent(new CustomEvent('themeChange', { detail: { theme: themeOption } }));
  };

  // Language update function
  const updateLanguagePreference = async (language) => {
    try {
      if (!user) return false;
      
      // Update i18next first for immediate feedback
      await i18n.changeLanguage(language);
      localStorage.setItem('preferredLanguage', language);
      
      // Then update in database
      const { error } = await supabase
        .from('profiles')
        .update({ language })
        .eq('id', user.id);
        
      if (error) {
        console.error('Error updating language preference:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Unexpected error updating language:', error);
      return false;
    }
  };
  
  // Theme update function
  const updateThemePreference = async (theme) => {
    try {
      // Update theme first for immediate feedback
      applyTheme(theme);
      localStorage.setItem('theme', theme);
      
      // If user is not logged in, just store in localStorage
      if (!user) return true;
      
      // Then update in database if user is logged in
      const { error } = await supabase
        .from('profiles')
        .update({ theme_preference: theme })
        .eq('id', user.id);
        
      if (error) {
        console.error('Error updating theme preference:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Unexpected error updating theme:', error);
      return false;
    }
  };

  // Profile refresh function - ADD THIS
  const refreshProfile = async () => {
    try {
      if (!user) return false;
      
      console.log('🔄 Refreshing user profile...');
      
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
        
      if (profileError) {
        console.error('Error refreshing profile:', profileError);
        return false;
      }
      
      if (profile) {
        setUserProfile(profile);
        console.log('✅ Profile refreshed with country:', profile.country_code);
        
        // Update language and theme if they changed
        if (profile.language) {
          await i18n.changeLanguage(profile.language);
          localStorage.setItem('preferredLanguage', profile.language);
        }
        
        if (profile.theme_preference) {
          applyTheme(profile.theme_preference);
          localStorage.setItem('theme', profile.theme_preference);
        }
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Unexpected error refreshing profile:', error);
      return false;
    }
  };

  // Create enriched user object with profile data
  // IMPORTANT: userProfile from Supabase may contain nested profile structure
  const userWithProfile = user && userProfile ? {
    ...user,
    profile: userProfile.profile || userProfile
  } : user;

  return (
    <AuthContext.Provider value={{ 
      user: userWithProfile, 
      loading, 
      userProfile,
      profileLoading, 
      updateLanguagePreference,
      updateThemePreference,
      refreshProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
};